import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { format, addMonths } from "date-fns";
import {
  FONTS,
  COLORS,
  BREAKPOINTS,
  SHADOWS,
} from "../../utils/styleConstants";
import PreviewModal from "./PreviewModal";
import { useAuth } from "../../context/AuthContext";
import { getOfferById, deleteOfferFile } from "../../services/api";
import RichTextEditor from "../common/RichTextEditor";
import { formatAmountForInput, cleanAmountValue, parseAmountToNumber } from '../../utils/amountUtils';
import useNetworkStatus from "../../hooks/useNetworkStatus";

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 2rem;
  flex: 1;
`;

const Title = styled.h1`
  color: ${COLORS.PRIMARY};
  font-size: 2.5rem;
  font-weight: 600;
  font-family: ${FONTS.PRIMARY};
  margin-bottom: 0.5rem;
`;

// Nuevo contenedor para las secciones horizontales
const HorizontalSections = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 1.5rem;
  padding-left: ${(props) => (props.isFirst ? "0" : "1.5rem")};
  border-radius: 8px;
  background-color: white;
`;

const FormWrapper = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const SectionTitle = styled.h2`
  font-size: 1.1rem;
  color: ${COLORS.SECONDARY};
  font-family: ${FONTS.SECONDARY};
  font-weight: 600;
  min-height: 2.5em;
  display: flex;
  align-items: center;
`;

const SectionDescription = styled.p`
  color: #666;
  font-size: 0.9rem;
  margin-bottom: 1.5rem;
  font-family: ${FONTS.SECONDARY};
  min-height: 3em;
`;

const HorizontalField = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: 1.5rem;
  position: relative;
  border-bottom: 1px solid #eaeaea;
  padding-bottom: 1rem;
  gap: 1rem;

  &:focus-within {
    border-bottom-color: ${COLORS.PRIMARY};
  }

  @media (max-width: ${BREAKPOINTS.MOBILE}) {
    flex-direction: column;
    align-items: flex-start;
    margin-bottom: 2rem;
    border-bottom: 1px solid #bad1e6;
  }
`;

const Label = styled.label`
  font-size: 0.9rem;
  color: ${COLORS.SECONDARY};
  font-family: ${FONTS.SECONDARY};
  font-weight: 500;
  display: flex;
  align-items: center;
  min-width: 40%;
  white-space: nowrap;

  @media (max-width: ${BREAKPOINTS.MOBILE}) {
    font-size: 0.9rem;
    margin-bottom: 0.5rem;
    width: 100%;
    white-space: normal;
  }
`;

const Input = styled.input`
  padding: 0.25rem 0;
  border: none;
  font-size: 0.9rem;
  font-family: ${FONTS.SECONDARY};
  outline: none;
  background-color: transparent;
  width: 100%;
  text-align: right;

  &:focus {
    outline: none;
  }

  &::placeholder {
    color: #c4c4c4;
    opacity: 1;
  }

  @media (max-width: ${BREAKPOINTS.MOBILE}) {
    font-size: 0.9rem;
    text-align: left;
    width: 100%;
  }
`;

const TextArea = styled.textarea`
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.9rem;
  font-family: ${FONTS.SECONDARY};
  min-height: 120px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: ${COLORS.PRIMARY};
    box-shadow: 0 0 0 2px rgba(255, 107, 53, 0.2);
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  position: relative;
  width: fit-content;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: 1.5rem;
  font-weight: 500;
  font-size: 0.95rem;
  font-family: ${FONTS.SECONDARY};
  cursor: pointer;
  transition: all 0.2s;

  &:focus {
    outline: none;
  }
`;

const SecondaryButton = styled(Button)`
  background-color: white;
  color: #a8b9d5;
  border: 1px solid #dbe4f2;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);

  &:hover {
    background-color: #f5f8fd;
  }

  &:disabled {
    color: #c4c4c4;
    border-color: #e2e2e2;
    cursor: not-allowed;
    box-shadow: none;
  }
`;

const SaveButton = styled(Button)`
  background-color: white;
  color: #ff7057;
  border: 1px solid #ff7057;
  box-shadow: 0 1px 2px rgba(255, 112, 87, 0.1);

  &:hover {
    background-color: #fff8f6;
  }

  &:disabled {
    color: #ffb8a8;
    border-color: #ffb8a8;
    cursor: not-allowed;
    box-shadow: none;
  }
`;

const PrimaryButton = styled(Button)`
  background-color: #ff7057;
  color: white;
  border: none;
  box-shadow: 0 2px 4px rgba(255, 112, 87, 0.2);

  &:hover {
    background-color: #ff5b40;
  }

  &:disabled {
    background-color: #ffc1ad;
    cursor: not-allowed;
    box-shadow: none;
  }
`;

const ToggleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-bottom: 1rem;
  border-bottom: 1px solid #eaeaea;
  padding-bottom: 1rem;
`;

const ToggleSwitch = styled.label`
  position: relative;
  display: inline-block;
  width: 44px;
  height: 22px;
  margin-right: 0;
`;

const ToggleInput = styled.input`
  opacity: 0;
  width: 0;
  height: 0;

  &:checked + span {
    background-color: #4d7af7;
  }

  &:checked + span:before {
    transform: translateX(22px);
  }
`;

const ToggleSlider = styled.span`
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #e2e2e2;
  transition: 0.3s;
  border-radius: 34px;

  &:before {
    position: absolute;
    content: "";
    height: 18px;
    width: 18px;
    left: 2px;
    bottom: 2px;
    background-color: white;
    transition: 0.3s;
    border-radius: 50%;
  }
`;

// Nuevo estilo para el tooltip similar al botón de ayuda
const InfoTooltip = styled.div`
  position: relative;
  display: inline-block;
  margin-left: 8px;

  &:hover span {
    visibility: visible;
    opacity: 1;
  }
`;

const HelpButton = styled.button`
  background-color: #75a9f9;
  color: white;
  border: none;
  border-radius: 50%;
  width: 12px;
  height: 12px;
  font-size: 0.6rem;
  font-family: ${FONTS.SECONDARY};
  font-weight: bold;
  cursor: help;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 8px;
  padding: 0;
  line-height: 1;
`;

const TooltipText = styled.span`
  visibility: hidden;
  width: auto;
  max-width: 450px;
  min-width: 400px;
  background-color: #c1d3f0;
  color: ${COLORS.SECONDARY};
  text-align: left;
  border-radius: 1rem;
  padding: 0.75rem 1.25rem;
  position: absolute;
  z-index: 100;
  bottom: 20px;
  left: -10px;
  opacity: 0;
  transition: opacity 0.3s;
  font-weight: normal;
  font-size: 0.85rem;
  font-family: ${FONTS.SECONDARY};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  line-height: 1.4;
  white-space: normal;
  word-wrap: break-word;

  &::after {
    content: "";
    position: absolute;
    bottom: -8px;
    left: 10px;
    width: 0;
    height: 0;
    border-left: 10px solid transparent;
    border-right: 10px solid transparent;
    border-top: 10px solid #c1d3f0;
  }
`;

// Actualizar el contenedor del HelpButton para posicionar correctamente el tooltip
const HelpButtonContainer = styled.div`
  position: relative;
  display: inline-flex;
  margin-left: 8px;
  flex-shrink: 0;

  &:hover ${TooltipText} {
    visibility: visible;
    opacity: 1;
  }
`;

const FileUploadContainer = styled.div`
  border: 2px dashed #ddd;
  padding: 2rem;
  border-radius: 4px;
  text-align: center;
  margin-bottom: 1.5rem;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: ${COLORS.PRIMARY};
    background-color: #fff8f6;
  }
`;

const UploadIcon = styled.div`
  font-size: 2rem;
  color: #ccc;
  margin-bottom: 1rem;
`;

const UploadText = styled.p`
  color: #666;
  margin-bottom: 0.5rem;
  font-family: ${FONTS.SECONDARY};
`;

const UploadHint = styled.p`
  color: #999;
  font-size: 0.8rem;
  font-family: ${FONTS.SECONDARY};
`;

const FileInputHidden = styled.input`
  display: none;
`;

const FilePreview = styled.div`
  display: flex;
  align-items: center;
  padding: 1rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  margin-top: 1rem;
`;

const FileIcon = styled.div`
  margin-right: 1rem;
  color: #ff6b35;
  font-size: 1.5rem;
`;

const FileName = styled.p`
  flex: 1;
  font-size: 0.9rem;
  color: #333;
`;

const RemoveFileButton = styled.button`
  background: none;
  border: none;
  color: #999;
  cursor: pointer;

  &:hover {
    color: #e41717;
  }
`;

const EditorToolbar = styled.div`
  display: flex;
  gap: 0.5rem;
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-bottom: none;
  border-radius: 4px 4px 0 0;
  background-color: #f5f5f5;
`;

const ToolbarButton = styled.button`
  background: none;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 0.25rem 0.5rem;
  cursor: pointer;

  &:hover {
    background-color: #eee;
  }

  &:focus {
    outline: none;
  }
`;

const CheckboxInput = styled.input`
  margin-right: 8px;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
  }
`;

const ConfirmationCheckbox = styled.div`
  display: flex;
  align-items: flex-start;
  width: 70%;

  label {
    font-size: 0.9rem;
    font-family: ${FONTS.SECONDARY};
    color: ${COLORS.SECONDARY};
    cursor: pointer;

    a {
      color: ${COLORS.PRIMARY};
      text-decoration: none;

      &:hover {
        text-decoration: underline;
      }
    }
  }
`;

const Checkbox = styled.input`
  margin-right: 0.5rem;
  margin-top: 0.1rem;
  transform: scale(0.8);
`;

const CheckboxLabel = styled.label`
  font-size: 0.75rem;
  color: #7986cb;
  line-height: 1.4;
  font-family: ${FONTS.SECONDARY};
  max-width: 95%;
  font-weight: normal;
  opacity: 0.7;
`;

const ErrorMessage = styled.div`
  color: ${COLORS.ERROR};
  font-size: 0.75rem;
  position: absolute;
  bottom: 0.3rem;
  right: 1rem;
  margin: 0;
  font-family: ${FONTS.SECONDARY};

  @media (max-width: ${BREAKPOINTS.MOBILE}) {
    bottom: -1.5rem;
    right: 0rem;
  }
`;

const TabsContainer = styled.div`
  display: flex;
  margin-bottom: 0;
`;

const Tab = styled.button`
  padding: 0.75rem 1.5rem;
  background-color: ${(props) => (props.active ? "#fff" : "#f5f5f5")};
  border: 1px solid #ddd;
  border-bottom: ${(props) => (props.active ? "none" : "1px solid #ddd")};
  border-radius: 4px 4px 0 0;
  margin-right: 0.5rem;
  font-weight: ${(props) => (props.active ? "600" : "400")};
  color: ${(props) => (props.active ? COLORS.PRIMARY : "#666")};
  cursor: pointer;
  font-family: ${FONTS.SECONDARY};

  &:focus {
    outline: none;
  }
`;

const TabContent = styled.div`
  border: 1px solid #ddd;
  border-radius: 0 4px 4px 4px;
  padding: 1.5rem;
`;

const InfoMessage = styled.div`
  padding: 0.75rem;
  border-radius: 4px;
  margin-top: 0.5rem;
  background-color: #fff8f6;
  border: 1px solid #ffcdb7;
  color: #b54800;
  font-size: 0.9rem;
  font-family: ${FONTS.SECONDARY};
`;

const PreviewButton = styled(Button)`
  background-color: transparent;
  color: #ff9980;
  border: none;
  border-radius: 0;
  text-decoration: underline;
  text-decoration-color: #ff9980;
  text-decoration-thickness: 2px;
  padding: 0.5rem 0.5rem;
  font-size: 0.9rem;
  font-weight: 500;
  margin: 0;

  &:hover {
    background-color: transparent;
    color: #ff7f66;
    text-decoration-color: #ff7f66;
  }
`;

const RequiredMessage = styled.p`
  font-size: 0.8rem;
  color: #666;
  margin-top: 1rem;
  font-family: ${FONTS.SECONDARY};
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
`;

const ClipIcon = styled.div`
  margin-right: 0.5rem;
  color: #75a9f9;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 16px;
    height: 16px;
    transform: rotate(-45deg);
  }
`;

const ArrowIcon = styled.div`
  margin-left: 0.5rem;
  color: #75a9f9;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 10px;
    height: 10px;
    fill: currentColor;
  }
`;

const AdjuntarButton = styled.button`
  display: flex;
  align-items: center;
  background-color: transparent;
  color: #75a9f9;
  border: none;
  font-size: 0.9rem;
  font-family: ${FONTS.SECONDARY};
  cursor: pointer;
  padding: 0.5rem 0;
  margin-bottom: 1rem;

  &:hover {
    text-decoration: underline;
  }
`;

const EditorArea = styled.textarea`
  width: 100%;
  min-height: 300px;
  border: 1px solid #75a9f9;
  border-radius: 1rem;
  padding: 1rem;
  font-size: 0.9rem;
  font-family: ${FONTS.SECONDARY};
  resize: none;
  margin-bottom: 0.5rem;

  &:focus {
    outline: none;
    border-color: #5a94f5;
  }

  &::placeholder {
    color: #a0aec0;
  }

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1d3f0;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #a8c1e9;
  }
`;

const ButtonArea = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  margin-top: 0;
  width: fit-content;
  margin-left: auto;
`;

const PreviewLinkContainer = styled.div`
  margin-bottom: 1rem;
`;

const StepIndicator = styled.p`
  font-size: 0.75rem;
  color: #ff7057;
  text-align: center;
  margin-top: 0.5rem;
  font-family: ${FONTS.SECONDARY};
  position: absolute;
  right: 0;
  bottom: -1.25rem;
  width: 140px; /* Ancho un poco mayor para asegurar espacio suficiente */
  white-space: nowrap;
`;

// Modificar el componente de Select para la confidencialidad
const SelectContainer = styled.div`
  position: relative;
  width: 100px;
`;

const Select = styled.select`
  appearance: none;
  width: 100%;
  padding: 8px 12px;
  font-size: 0.9rem;
  border: none;
  border-radius: 4px;
  background-color: white;
  color: ${COLORS.TEXT};
  font-family: ${FONTS.SECONDARY};
  cursor: pointer;

  &:focus {
    outline: none;
  }

  @media (max-width: ${BREAKPOINTS.MOBILE}) {
    width: 100%;
  }
`;

const SelectArrow = styled.div`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;

  svg {
    width: 12px;
    height: 12px;
    fill: ${COLORS.SECONDARY};
  }
`;

// Crear un estilo para el botón deshabilitado
const DisabledAdjuntarButton = styled(AdjuntarButton)`
  opacity: 0.5;
  cursor: not-allowed;

  &:hover {
    text-decoration: none;
  }
`;

// Crear un estilo para el editor deshabilitado
const DisabledEditorArea = styled(EditorArea)`
  opacity: 0.5;
  cursor: not-allowed;
  background-color: #f9f9f9;
`;

// Componente principal
const OfertaStep2 = ({
  formData,
  updateFormData,
  prevStep,
  saveDraft,
  onSubmit,
  isSubmitting,
  isSavingDraft,
  isReadOnly = false,
  hasShownNetworkModal = false,
  isOnline = true,
}) => {
  const getDefaultDeadlineDate = () => {
    const today = new Date();
    const oneMonthFromNow = addMonths(today, 1);
    return format(oneMonthFromNow, "yyyy-MM-dd");
  };
  const { user } = useAuth();
  // State setup with proper defaults
  const [referencia, setReferencia] = useState(formData.referencia || "");
  const [objeto, setObjeto] = useState(formData.objeto || "");
  const [cuantia, setCuantia] = useState(formData.cuantia || "");
  const [confidencialidad, setConfidencialidad] = useState(
    formData.confidencialidad === undefined
      ? true
      : Boolean(formData.confidencialidad)
  );
  const [plazoRespuesta, setPlazoRespuesta] = useState(
    formData.plazoRespuesta || getDefaultDeadlineDate()
  );
  const [contenido, setContenido] = useState(formData.contenido || "");
  const [archivo, setArchivo] = useState(formData.archivo);
  const [aceptaCondiciones, setAceptaCondiciones] = useState(
    formData.aceptaCondiciones
  );
  // Estado para archivos existentes de la API
  const [existingFiles, setExistingFiles] = useState([]);
  // Estado para controlar la carga de archivos
  const [isDeletingFile, setIsDeletingFile] = useState(false);
  
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({
    referencia: false,
    objeto: false,
    cuantia: false,
    plazoRespuesta: false,
    contenido: false,
    archivo: false,
    aceptaCondiciones: false,
  });
  const [isFormValid, setIsFormValid] = useState(false);
  const [showConfidentialityWarning, setShowConfidentialityWarning] = useState(
    !confidencialidad
  );
  const [showPreview, setShowPreview] = useState(false);

  // Usar las funciones unificadas de amountUtils
  const formatCuantia = formatAmountForInput;
  const cleanCuantiaValue = cleanAmountValue;

  // Función helper para detectar si el contenido del RichTextEditor está realmente vacío
  const isRichTextEmpty = (htmlContent) => {
    if (!htmlContent) return true;
    
    // Crear un elemento temporal para parsear el HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;
    
    // Obtener solo el texto sin tags HTML
    const textContent = tempDiv.textContent || tempDiv.innerText || '';
    
    // Verificar si el texto está vacío después de quitar espacios y saltos de línea
    return textContent.trim().length === 0;
  };

  // Función para manejar el cambio en el campo de cuantía
  const handleCuantiaChange = (e) => {
    const rawValue = e.target.value;
    
    // Permitir entrada libre sin formateo automático
    // Solo actualizar el estado visual, no el formData
    setCuantia(rawValue);
    
    // No actualizar formData aquí, solo al perder el foco
  };

  // Función para manejar el blur del campo de cuantía (formateo final)
  const handleCuantiaBlur = () => {
    if (cuantia) {
      const formattedValue = formatCuantia(cuantia);
      setCuantia(formattedValue);
      
      const cleanedValue = cleanCuantiaValue(formattedValue);
      updateFormData("cuantia", cleanedValue);
    }
    handleBlur("cuantia");
  };

  // Modificar el useEffect que responde a cambios en formData para que
  // solo se ejecute una vez al inicio (montaje) utilizando una referencia para rastrear esto
  const isInitialMount = useRef(true);

  // Referencia para almacenar el ID de la oferta si existe
  const offerIdRef = useRef(null);

  // Detectar si estamos editando una oferta existente (con ID)
  useEffect(() => {
    // Intentar obtener el ID de la URL (similar a como se hace en NuevaOfertaPage)
    const currentUrl = window.location.pathname;
    const match = currentUrl.match(/\/editar-oferta\/([^\/]+)/);
    if (match && match[1]) {
      offerIdRef.current = match[1];
      console.log("ID de oferta detectado:", offerIdRef.current);
    }
  }, []);

  // Cargar archivos existentes cuando tengamos un ID de oferta
  useEffect(() => {
    const loadExistingFiles = async () => {
      if (offerIdRef.current && formData.files) {
        // Si ya tenemos archivos en formData, usarlos
        setExistingFiles(formData.files || []);
      } else if (offerIdRef.current) {
        // Si no tenemos archivos en formData pero sí un ID, cargar desde API
        try {
          const offerData = await getOfferById(offerIdRef.current);
          if (offerData.files) {
            setExistingFiles(offerData.files);
          }
        } catch (error) {
          console.error("Error loading existing files:", error);
        }
      }
    };

    loadExistingFiles();
  }, [formData.files, offerIdRef.current]);

  // Función para eliminar archivo existente
  const handleDeleteExistingFile = async (fileId) => {
    if (!offerIdRef.current) {
      console.error("No offer ID available for file deletion");
      return;
    }

    setIsDeletingFile(true);
    try {
      const response = await deleteOfferFile(offerIdRef.current, fileId);
      console.log("Archivo eliminado:", response.deletedFile);
      
      // Actualizar la lista de archivos existentes con los archivos restantes
      setExistingFiles(response.remainingFiles || []);
      
      // También actualizar formData si es necesario
      updateFormData("files", response.remainingFiles || []);
      
    } catch (error) {
      console.error("Error deleting file:", error);
      setErrors(prev => ({
        ...prev,
        archivo: error.message || "Error al eliminar el archivo"
      }));
    } finally {
      setIsDeletingFile(false);
    }
  };

  // Update state when formData changes (for example when editing an existing offer)
  useEffect(() => {
    // Solo cargar datos de formData la primera vez
    if (isInitialMount.current) {
      console.log("OfertaStep2 cargando datos iniciales:", formData);
      setReferencia(formData.referencia || "");
      setObjeto(formData.objeto || "");
      
      // Aplicar formato al campo de cuantía si existe un valor
      if (formData.cuantia) {
        const formattedCuantia = formatCuantia(formData.cuantia.toString());
        setCuantia(formattedCuantia);
      } else {
        setCuantia("");
      }
      
      setConfidencialidad(
        formData.confidencialidad === undefined
          ? true
          : Boolean(formData.confidencialidad)
      );

      // Set deadline with fallback to one month from now
      if (formData.plazoRespuesta) {
        try {
          // Parse the date correctly regardless of input format
          const dateValue = new Date(formData.plazoRespuesta);

          // Check if we have a valid date
          if (!isNaN(dateValue.getTime())) {
            // Format to YYYY-MM-DD for HTML date input
            const formattedDate = format(dateValue, "yyyy-MM-dd");
            console.log(
              `Formatting plazoRespuesta from ${formData.plazoRespuesta} to ${formattedDate}`
            );
            setPlazoRespuesta(formattedDate);
          } else {
            console.warn(
              `Invalid date received from API: ${formData.plazoRespuesta}`
            );
            setPlazoRespuesta(getDefaultDeadlineDate());
          }
        } catch (error) {
          console.error("Error formatting deadline date:", error);
          setPlazoRespuesta(getDefaultDeadlineDate());
        }
      } else {
        setPlazoRespuesta(getDefaultDeadlineDate());
      }

      setContenido(formData.contenido || "");
      setArchivo(formData.archivo);

      // Marcar que ya se ha cargado inicialmente
      isInitialMount.current = false;
    }
  }, [formData]);

  // Handle field blur to mark fields as touched
  const handleBlur = (field) => {
    setTouched({
      ...touched,
      [field]: true,
    });
  };

  // Validar formulario cuando cambian los campos
  useEffect(() => {
    validateForm();
  }, [
    referencia,
    objeto,
    cuantia,
    plazoRespuesta,
    contenido,
    archivo,
    aceptaCondiciones,
    existingFiles,
  ]);

  const validateForm = () => {
    const newErrors = {};

    // Validación de objeto de la controversia
    if (!objeto) {
      newErrors.objeto = "El objeto de la controversia es obligatorio";
    }

    // Validación de cuantía
    if (!cuantia) {
      newErrors.cuantia = "La cuantía total es obligatoria";
    } else {
      const cleanValue = cleanCuantiaValue(cuantia);
      if (!cleanValue || isNaN(parseAmountToNumber(cleanValue))) {
        newErrors.cuantia = "La cuantía debe ser un número válido";
      }
    }

    // Validación de plazo de respuesta
    if (!plazoRespuesta) {
      newErrors.plazoRespuesta = "El plazo de respuesta es obligatorio";
    } else {
      // Create normalized dates for comparison (without time component)
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // Create a min date that's exactly one month in the future
      const minDate = new Date(today);
      minDate.setMonth(today.getMonth() + 1);

      // Parse the selected date without time component
      const selectedDate = new Date(plazoRespuesta);
      selectedDate.setHours(0, 0, 0, 0);

      // Calculate difference in milliseconds
      const diffTime = selectedDate.getTime() - minDate.getTime();
      // Convert to days for more readable comparison
      const diffDays = diffTime / (1000 * 60 * 60 * 24);

      console.log("Date validation:", {
        today: today.toISOString().split("T")[0],
        minDate: minDate.toISOString().split("T")[0],
        selectedDate: selectedDate.toISOString().split("T")[0],
        diffDays: diffDays,
        isValid: diffDays >= -1, // Allow a 1-day tolerance
      });

      // Only show error if the date is more than 1 day before the minimum
      if (diffDays < -1) {
        newErrors.plazoRespuesta =
          "El plazo debe ser de al menos 1 mes a partir de hoy";
      }
    }

    // Validación de contenido o archivo
    if (!archivo && isRichTextEmpty(contenido) && (!existingFiles || existingFiles.length === 0)) {
      newErrors.contenido = "Debe redactar la oferta o adjuntar un documento";
    }

    // Validación de aceptación de condiciones
    if (!aceptaCondiciones) {
      newErrors.aceptaCondiciones = "Debe aceptar las condiciones";
    }

    setErrors(newErrors);
    setIsFormValid(Object.keys(newErrors).length === 0);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return; // Si no hay archivo seleccionado, no hacer nada

    setTouched({
      ...touched,
      archivo: true,
    });

    const validTypes = [
      "application/pdf",
    ];

    if (validTypes.includes(file.type)) {
      // Limpiar el contenido del editor cuando se adjunta un archivo
      setContenido("");

      // Establecer el archivo y actualizar el estado global de forma segura
      setArchivo(file);
      console.log("Archivo adjuntado:", file.name);

      // Actualizar formData con los nuevos valores
      updateFormData("contenido", "");
      updateFormData("archivo", file);
    } else {
      setErrors({
        ...errors,
        archivo: "Solo se admiten archivos PDF",
      });
    }
  };

  const handleRemoveFile = () => {
    setArchivo(null);
    setTouched({
      ...touched,
      archivo: true,
    });

    // Actualizar formData
    updateFormData("archivo", null);
    console.log("Archivo eliminado");
  };

  // Modificar la función para trabajar con el select y mantener consistencia
  const handleToggleConfidentiality = (e) => {
    const isConfidential = e.target.value === "true";

    console.log("🔍 CONFIDENCIALIDAD DEBUG:", {
      valorSelect: e.target.value,
      isConfidential: isConfidential,
      estadoAnterior: confidencialidad,
      timestamp: new Date().toISOString()
    });

    // Actualizar estado local
    setConfidencialidad(isConfidential);

    // Actualizar estado global inmediatamente
    updateFormData("confidencialidad", isConfidential);

    // Actualizar advertencia
    setShowConfidentialityWarning(!isConfidential);

    console.log("Confidencialidad cambiada a:", isConfidential);
  };

  // Simplificar el detector de autocompletado para evitar conflictos
  useEffect(() => {
    // Detector de autorellenado para todos los campos
    const autoFillListener = () => {
      const inputs = document.querySelectorAll(
        'input:not([type="checkbox"]):not([type="file"])'
      );
      let stateChanged = false;

      inputs.forEach((input) => {
        // Detectar autorellenado
        const isAutoFilled =
          getComputedStyle(input).getPropertyValue("background-color") !==
            "rgb(255, 255, 255)" || input.matches(":-webkit-autofill");

        if (isAutoFilled && input.value) {
          console.log(
            `Detected autofill on input (Step 2): ${input.id || input.name}`
          );
          stateChanged = true;

          // Solo actualizar inputs básicos, no los que tienen manejo especial
          if (input.id === "referencia") {
            setReferencia(input.value);
            updateFormData("referencia", input.value);
          } else if (input.id === "objeto") {
            setObjeto(input.value);
            updateFormData("objeto", input.value);
          } else if (input.id === "cuantia") {
            setCuantia(input.value);
            updateFormData("cuantia", input.value);
          } else if (input.id === "plazoRespuesta") {
            try {
              // Ensure proper date format for the input field
              const dateValue = new Date(input.value);

              if (!isNaN(dateValue.getTime())) {
                // Format to YYYY-MM-DD for HTML date input
                const formattedDate = format(dateValue, "yyyy-MM-dd");
                setPlazoRespuesta(formattedDate);
                updateFormData("plazoRespuesta", formattedDate);
              }
            } catch (error) {
              console.error("Error formatting autofilled date:", error);
            }
          }
        }
      });
    };

    // Solo ejecutar una vez, no continuamente para evitar conflictos
    const timeoutId = setTimeout(autoFillListener, 1000);

    return () => {
      clearTimeout(timeoutId);
    };
  }, []);

  const formatDateValue = (dateString) => {
    try {
      const dateValue = new Date(dateString);
      if (!isNaN(dateValue.getTime())) {
        return format(dateValue, "yyyy-MM-dd");
      }
      return dateString;
    } catch (error) {
      console.error("Error formatting date:", error);
      return dateString;
    }
  };

  const handleSaveDraft = async () => {
    try {
      // Capturar los valores actualizados desde el estado local
      const capturarValoresReales = () => {
        // Capturar cada valor desde el estado o del DOM para campos simples
        const referenciaActual =
          document.getElementById("referencia")?.value || referencia;
        const objetoActual = document.getElementById("objeto")?.value || objeto;
        const cuantiaActual =
          document.getElementById("cuantia")?.value || cuantia;

        // Format the date properly
        const plazoRespuestaDom =
          document.getElementById("plazoRespuesta")?.value || plazoRespuesta;
        const plazoRespuestaActual = formatDateValue(plazoRespuestaDom);

        const confidencialidadActual = confidencialidad; // Este es un toggle, se maneja diferente

        // Para el contenido del editor de texto enriquecido, usar el estado local
        const contenidoActual = contenido;
        const aceptaCondicionesActual =
          document.getElementById("terms-checkbox")?.checked ||
          aceptaCondiciones;

        // Actualizar los estados locales inmediatamente
        setReferencia(referenciaActual);
        setObjeto(objetoActual);
        setCuantia(cuantiaActual);
        setPlazoRespuesta(plazoRespuestaActual);
        setContenido(contenidoActual);
        setAceptaCondiciones(aceptaCondicionesActual);

        return {
          referenciaActual,
          objetoActual,
          cuantiaActual,
          plazoRespuestaActual,
          confidencialidadActual,
          contenidoActual,
          aceptaCondicionesActual,
        };
      };

      // Capturar valores actualizados del paso 2
      const {
        referenciaActual,
        objetoActual,
        cuantiaActual,
        plazoRespuestaActual,
        confidencialidadActual,
        contenidoActual,
        aceptaCondicionesActual,
      } = capturarValoresReales();

      // Verificar si tenemos un ID para recuperar datos actualizados de la API
      const offerId = offerIdRef.current;
      let datosActualizados = null;

      if (offerId) {
        try {
          console.log(
            "Recuperando datos actualizados de la API para ID:",
            offerId
          );
          // Obtener datos actualizados de la API
          const offerData = await getOfferById(offerId);
          console.log("Datos recuperados de la API:", offerData);

          // Log para verificar el estado de las identificaciones
          console.log("Estado de cifrado de las identificaciones:", {
            ofertanteID: {
              valor:
                offerData.offeror?.idValue ||
                offerData.representedparty_id ||
                "",
              cifrado: isIdEncrypted(
                offerData.offeror?.idValue || offerData.representedparty_id
              ),
            },
            contraparteID: {
              valor:
                offerData.counterparty?.idValue ||
                offerData.counterparty_id ||
                "",
              cifrado: isIdEncrypted(
                offerData.counterparty?.idValue || offerData.counterparty_id
              ),
            },
            representacionID: {
              valor: offerData.lawyer?.idValue || offerData.lawyer_id || "",
              cifrado: isIdEncrypted(
                offerData.lawyer?.idValue || offerData.lawyer_id
              ),
            },
          });

          // Helper para verificar si un ID parece estar cifrado
          function isIdEncrypted(id) {
            return id && typeof id === "string" && /^[a-f0-9]{32,}$/i.test(id);
          }

          // Extraer solo los datos del paso 1 (ofertante, contraparte, representación)
          datosActualizados = {
            ofertante: {
              nombre:
                offerData.offeror?.firstName ||
                offerData.representedparty_name ||
                "",
              apellidos:
                offerData.offeror?.lastNames ||
                offerData.representedparty_last_name ||
                "",
              email:
                offerData.offeror?.email ||
                offerData.representedparty_email ||
                "",
              identificacion:
                offerData.offeror?.idValue ||
                offerData.representedparty_id ||
                "",
              movil:
                offerData.offeror?.mobile ||
                offerData.representedparty_phone ||
                "",
            },
            contraparte: {
              nombre:
                offerData.counterparty?.firstName ||
                offerData.counterparty_name ||
                "",
              apellidos:
                offerData.counterparty?.lastNames ||
                offerData.counterparty_last_name ||
                "",
              email:
                offerData.counterparty?.email ||
                offerData.counterparty_email ||
                "",
              identificacion:
                offerData.counterparty?.idValue ||
                offerData.counterparty_id ||
                "",
              movil:
                offerData.counterparty?.mobile ||
                offerData.counterparty_phone ||
                "",
            },
            representacionContraparte: {
              activa:
                !!offerData.lawyer?.firstName ||
                !!offerData.lawyer_name ||
                false,
              nombre:
                offerData.lawyer?.firstName || offerData.lawyer_name || "",
              apellidos:
                offerData.lawyer?.lastNames || offerData.lawyer_last_name || "",
              email: offerData.lawyer?.email || offerData.lawyer_email || "",
              identificacion:
                offerData.lawyer?.idValue || offerData.lawyer_id || "",
              movil: offerData.lawyer?.mobile || offerData.lawyer_phone || "",
            },
          };

          console.log("Datos del paso 1 extraídos de la API:", {
            ofertanteNombre: datosActualizados.ofertante.nombre,
            ofertanteID: datosActualizados.ofertante.identificacion,
            contraparteNombre: datosActualizados.contraparte.nombre,
            contraparteID: datosActualizados.contraparte.identificacion,
          });
        } catch (error) {
          console.error("Error al recuperar datos de la API:", error);
          // Si hay error, seguimos con los datos actuales de formData
        }
      }

      // Si no tenemos datos actualizados de la API o hubo un error, usamos los datos actuales
      const ofertanteActual = datosActualizados
        ? datosActualizados.ofertante
        : formData.ofertante || {};
      const contraparteActual = datosActualizados
        ? datosActualizados.contraparte
        : formData.contraparte || {};
      const representacionActual = datosActualizados
        ? datosActualizados.representacionContraparte
        : formData.representacionContraparte || { activa: false };

      // Verificar explícitamente que los datos de identificación se conservan
      console.log("IDENTIFICACIONES DISPONIBLES PARA GUARDAR:", {
        ofertanteID: ofertanteActual.identificacion || "NO DISPONIBLE",
        contraparteID: contraparteActual.identificacion || "NO DISPONIBLE",
        representacionID: representacionActual.activa
          ? representacionActual.identificacion
          : "N/A",
      });

      // Determinar si hay archivos (nuevos o existentes)
      const hasFiles = !!(archivo || (existingFiles && existingFiles.length > 0));
      console.log("DETERMINANDO TIPO DE GUARDADO:", {
        archivoNuevo: !!archivo,
        archivosExistentes: existingFiles?.length || 0,
        hasFiles: hasFiles,
        tipoGuardado: hasFiles ? "FormData" : "JSON"
      });

      // Crear un objeto con los valores combinados: paso 1 (API o formData) + paso 2 (DOM actual)
      const currentValues = {
        // Datos del paso 1 (de la API o formData)
        ofertante: ofertanteActual,
        contraparte: contraparteActual,
        representacionContraparte: representacionActual,
        // Valores actualizados para este paso
        referencia: referenciaActual,
        objeto: objetoActual,
        cuantia: cuantiaActual,
        confidencialidad: confidencialidadActual,
        plazoRespuesta: plazoRespuestaActual,
        contenido: contenidoActual,
        archivo,
        aceptaCondiciones: aceptaCondicionesActual,
        // Agregar archivos existentes y información de archivos
        existingFiles,
        files: existingFiles,  // Para compatibilidad con la API
        hasFiles,  // Información para el componente padre
      };

      // Verificar los datos antes de guardar
      console.log("DATOS COMPLETOS COMBINADOS A GUARDAR:", {
        ofertanteNombre: currentValues.ofertante?.nombre || "NO PRESENTE",
        ofertanteIdentificacion:
          currentValues.ofertante?.identificacion || "NO PRESENTE",
        contraparteIdentificacion:
          currentValues.contraparte?.identificacion || "NO PRESENTE",
        objeto: currentValues.objeto,
        cuantia: currentValues.cuantia,
      });

      // Llamar a la función para guardar borrador con los valores actualizados
      await saveDraft(currentValues);

      // Actualizar la referencia del ID si se creó una nueva oferta
      if (!offerIdRef.current) {
        // Intentar extraer el ID de la nueva URL si se creó una oferta
        const currentUrl = window.location.pathname;
        const match = currentUrl.match(/\/editar-oferta\/([^\/]+)/);
        if (match && match[1]) {
          offerIdRef.current = match[1];
          console.log("Nuevo ID de oferta detectado:", offerIdRef.current);
        }
      }
    } catch (error) {
      console.error("Error al guardar el borrador:", error);
    }
  };

  const handleSubmit = async () => {
    // Mark all fields as touched on submit attempt
    const allTouched = Object.keys(touched).reduce((acc, field) => {
      acc[field] = true;
      return acc;
    }, {});
    setTouched(allTouched);

    if (isFormValid) {
      try {
        // Capture all form values before submission
        const captureCurrentValues = () => {
          // Get values from DOM for simple fields or state for complex ones
          const referenciaActual =
            document.getElementById("referencia")?.value || referencia;
          const objetoActual =
            document.getElementById("objeto")?.value || objeto;
          const cuantiaActual =
            document.getElementById("cuantia")?.value || cuantia;

          // Format the date properly
          const plazoRespuestaDom =
            document.getElementById("plazoRespuesta")?.value || plazoRespuesta;
          const plazoRespuestaActual = formatDateValue(plazoRespuestaDom);

          // For rich text editor, use the local state
          const editorContent = contenido;
          const aceptaCondicionesActual =
            document.getElementById("terms-checkbox")?.checked ||
            aceptaCondiciones;

          return {
            referenciaActual,
            objetoActual,
            cuantiaActual,
            plazoRespuestaActual,
            editorContent,
            aceptaCondicionesActual,
          };
        };

        // Get fresh values from step 2
        const {
          referenciaActual,
          objetoActual,
          cuantiaActual,
          plazoRespuestaActual,
          editorContent,
          aceptaCondicionesActual,
        } = captureCurrentValues();

        // Update local state with captured values
        setReferencia(referenciaActual);
        setObjeto(objetoActual);
        setCuantia(cuantiaActual);
        setPlazoRespuesta(plazoRespuestaActual);
        setContenido(editorContent);
        setAceptaCondiciones(aceptaCondicionesActual);

        // Verificar si tenemos un ID para recuperar datos actualizados de la API
        const offerId = offerIdRef.current;
        let datosActualizados = null;

        if (offerId) {
          try {
            console.log(
              "Recuperando datos actualizados de la API para envío, ID:",
              offerId
            );
            // Obtener datos actualizados de la API
            const offerData = await getOfferById(offerId);
            console.log("Datos para envío recuperados de la API:", offerData);

            // Log para verificar el estado de las identificaciones
            console.log(
              "Estado de cifrado de las identificaciones para envío:",
              {
                ofertanteID: {
                  valor:
                    offerData.offeror?.idValue ||
                    offerData.representedparty_id ||
                    "",
                  cifrado: isIdEncrypted(
                    offerData.offeror?.idValue || offerData.representedparty_id
                  ),
                },
                contraparteID: {
                  valor:
                    offerData.counterparty?.idValue ||
                    offerData.counterparty_id ||
                    "",
                  cifrado: isIdEncrypted(
                    offerData.counterparty?.idValue || offerData.counterparty_id
                  ),
                },
                representacionID: {
                  valor: offerData.lawyer?.idValue || offerData.lawyer_id || "",
                  cifrado: isIdEncrypted(
                    offerData.lawyer?.idValue || offerData.lawyer_id
                  ),
                },
              }
            );

            // Helper para verificar si un ID parece estar cifrado
            function isIdEncrypted(id) {
              return (
                id && typeof id === "string" && /^[a-f0-9]{32,}$/i.test(id)
              );
            }

            // Extraer solo los datos del paso 1 (ofertante, contraparte, representación)
            datosActualizados = {
              ofertante: {
                nombre:
                  offerData.offeror?.firstName ||
                  offerData.representedparty_name ||
                  "",
                apellidos:
                  offerData.offeror?.lastNames ||
                  offerData.representedparty_last_name ||
                  "",
                email:
                  offerData.offeror?.email ||
                  offerData.representedparty_email ||
                  "",
                identificacion:
                  offerData.offeror?.idValue ||
                  offerData.representedparty_id ||
                  "",
                movil:
                  offerData.offeror?.mobile ||
                  offerData.representedparty_phone ||
                  "",
              },
              contraparte: {
                nombre:
                  offerData.counterparty?.firstName ||
                  offerData.counterparty_name ||
                  "",
                apellidos:
                  offerData.counterparty?.lastNames ||
                  offerData.counterparty_last_name ||
                  "",
                email:
                  offerData.counterparty?.email ||
                  offerData.counterparty_email ||
                  "",
                identificacion:
                  offerData.counterparty?.idValue ||
                  offerData.counterparty_id ||
                  "",
                movil:
                  offerData.counterparty?.mobile ||
                  offerData.counterparty_phone ||
                  "",
              },
              representacionContraparte: {
                activa:
                  !!offerData.lawyer?.firstName ||
                  !!offerData.lawyer_name ||
                  false,
                nombre:
                  offerData.lawyer?.firstName || offerData.lawyer_name || "",
                apellidos:
                  offerData.lawyer?.lastNames || offerData.lawyer_last_name || "",
                email: offerData.lawyer?.email || offerData.lawyer_email || "",
                identificacion:
                  offerData.lawyer?.idValue || offerData.lawyer_id || "",
                movil: offerData.lawyer?.mobile || offerData.lawyer_phone || "",
              },
            };

            console.log("Datos del paso 1 para envío extraídos de la API:", {
              ofertanteNombre: datosActualizados.ofertante.nombre,
              ofertanteID: datosActualizados.ofertante.identificacion,
              contraparteNombre: datosActualizados.contraparte.nombre,
              contraparteID: datosActualizados.contraparte.identificacion,
            });
          } catch (error) {
            console.error(
              "Error al recuperar datos de la API para envío:",
              error
            );
            // Si hay error, seguimos con los datos actuales de formData
          }
        }

        // Si no tenemos datos actualizados de la API o hubo un error, usamos los datos actuales
        const ofertanteActual = datosActualizados
          ? datosActualizados.ofertante
          : formData.ofertante || {};
        const contraparteActual = datosActualizados
          ? datosActualizados.contraparte
          : formData.contraparte || {};
        const representacionActual = datosActualizados
          ? datosActualizados.representacionContraparte
          : formData.representacionContraparte || { activa: false };

        // Verificar explícitamente que los datos de identificación se conservan
        console.log("IDENTIFICACIONES DISPONIBLES PARA ENVÍO:", {
          ofertanteID: ofertanteActual.identificacion || "NO DISPONIBLE",
          contraparteID: contraparteActual.identificacion || "NO DISPONIBLE",
          representacionID: representacionActual.activa
            ? representacionActual.identificacion
            : "N/A",
        });

        // Determinar si hay archivos (nuevos o existentes)
        const hasFiles = !!(archivo || (existingFiles && existingFiles.length > 0));
        console.log("DETERMINANDO TIPO DE ENVÍO:", {
          archivoNuevo: !!archivo,
          archivosExistentes: existingFiles?.length || 0,
          hasFiles: hasFiles,
          tipoEnvio: hasFiles ? "FormData" : "JSON"
        });

        // Crear un objeto con los valores actualizados para enviar al componente padre
        const currentValues = {
          // Datos del paso 1 (de la API o formData)
          ofertante: ofertanteActual,
          contraparte: contraparteActual,
          representacionContraparte: representacionActual,
          // Valores actualizados para este paso
          referencia: referenciaActual,
          objeto: objetoActual,
          cuantia: cuantiaActual,
          confidencialidad,
          plazoRespuesta: plazoRespuestaActual,
          contenido: editorContent,
          archivo,
          aceptaCondiciones: aceptaCondicionesActual,
          // Agregar archivos existentes y información de archivos
          existingFiles,
          files: existingFiles,  // Para compatibilidad con la API
          hasFiles,  // Información para el componente padre
        };

        console.log("🔍 CONFIDENCIALIDAD EN ENVÍO:", {
          confidencialidadLocal: confidencialidad,
          confidencialidadCurrentValues: currentValues.confidencialidad,
          tipoConfidencialidad: typeof confidencialidad,
          valorBooleano: Boolean(confidencialidad),
          timestamp: new Date().toISOString()
        });

        // Verificar que todos los objetos necesarios existan antes de enviar
        console.log("DATOS COMPLETOS COMBINADOS A ENVIAR:", {
          ofertanteNombre: currentValues.ofertante?.nombre || "NO PRESENTE",
          ofertanteIdentificacion:
            currentValues.ofertante?.identificacion || "NO PRESENTE",
          contraparteIdentificacion:
            currentValues.contraparte?.identificacion || "NO PRESENTE",
          objeto: currentValues.objeto,
          cuantia: currentValues.cuantia,
        });

        // Llamar directamente a la función de envío con los valores actualizados
        await onSubmit(currentValues);
      } catch (error) {
        console.error("Error al enviar la oferta:", error);
      }
    }
  };

  const handlePreview = () => {
    // Capturar el estado actual de los datos del formulario
    console.log("Mostrando vista previa con datos actuales:");
    console.log("- objeto:", objeto);
    console.log("- confidencialidad:", confidencialidad);
    console.log("- contenido:", contenido);

    // Actualizar formData para asegurar datos actualizados antes de mostrar la vista previa
    updateFormData("referencia", referencia);
    updateFormData("objeto", objeto);
    updateFormData("cuantia", cuantia);
    updateFormData("confidencialidad", confidencialidad);
    updateFormData("plazoRespuesta", plazoRespuesta);
    updateFormData("contenido", contenido);
    updateFormData("archivo", archivo);
    updateFormData("aceptaCondiciones", aceptaCondiciones);

    // Mostrar la vista previa
    setShowPreview(true);
  };

  // Función para aplicar formato al editor
  const applyFormat = (format) => {
    const textarea = document.getElementById("oferta-content");
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = textarea.value.substring(start, end);

    let formattedText = "";
    switch (format) {
      case "bold":
        formattedText = `**${selectedText}**`;
        break;
      case "underline":
        formattedText = `__${selectedText}__`;
        break;
      case "bullet":
        formattedText = `• ${selectedText}`;
        break;
      default:
        formattedText = selectedText;
    }

    const newText =
      textarea.value.substring(0, start) +
      formattedText +
      textarea.value.substring(end);
    setContenido(newText);

    // Restaurar foco y selección
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start, start + formattedText.length);
    }, 0);
  };

  // Helper to process content for preview
  const getPreviewContent = () => {
    if (!archivo && contenido) {
      // ReactQuill always returns HTML, so we can return it directly
      return contenido;
    }
    return "";
  };

  const renderField = (
    field,
    label,
    value,
    onChange,
    error,
    type = "text",
    required = true,
    tooltip = null
  ) => {
    // Calculate minimum date (today + 1 month) for date inputs
    const today = new Date();
    const minDate = new Date(today);
    minDate.setMonth(today.getMonth() + 1);
    const minDateFormatted = format(minDate, "yyyy-MM-dd");

    // For date inputs, ensure the value is not before the minimum date
    const handleDateChange = (e) => {
      const selectedDate = new Date(e.target.value);
      const minDate = new Date(today);
      minDate.setMonth(today.getMonth() + 1);

      if (selectedDate < minDate) {
        // If selected date is before minimum, set to minimum date
        const formattedMinDate = format(minDate, "yyyy-MM-dd");
        onChange({ target: { value: formattedMinDate } });
      } else {
        onChange(e);
      }
    };

    // Determinar el manejador de blur específico para cada campo
    const getBlurHandler = () => {
      if (field === "cuantia") {
        return handleCuantiaBlur;
      }
      return () => handleBlur(field);
    };

    return (
      <HorizontalField>
        <Label htmlFor={field} required={required}>
          {label}
          {tooltip && (
            <HelpButtonContainer>
              <HelpButton type="button">i</HelpButton>
              <TooltipText>{tooltip}</TooltipText>
            </HelpButtonContainer>
          )}
        </Label>
        <Input
          type={type}
          id={field}
          value={value}
          onChange={type === "date" ? handleDateChange : onChange}
          placeholder={getPlaceholder(field, type)}
          style={type === "date" ? { direction: "rtl" } : {}}
          onBlur={getBlurHandler()}
          readOnly={isReadOnly}
          min={type === "date" ? minDateFormatted : undefined}
        />
        {error && touched[field] && <ErrorMessage>{error}</ErrorMessage>}
      </HorizontalField>
    );
  };

  // Función para obtener el placeholder según el campo
  const getPlaceholder = (field, type) => {
    if (type !== "text") return undefined;

    switch (field) {
      case "referencia":
        return "Escribe un identificador personal o referencia de esta oferta (opcional)";
      case "objeto":
        return "Escribe aquí el motivo de la controversia";
      case "cuantia":
        return "0,00 €";
      default:
        return "";
    }
  };

  // Modificar la función renderConfidentialityToggle
  const renderConfidentialityToggle = () => {
    return (
      <ToggleContainer>
        <Label htmlFor="confidencialidad" required style={{ minWidth: "auto" }}>
          Confidencialidad
          <HelpButtonContainer>
            <HelpButton type="button">i</HelpButton>
            <TooltipText>
              El artículo 9.2 a) de la L.O. 1/2025 establece que sólo puede
              dispensarse la confidencialidad cuando todas las partes (o sus
              abogados/as) de manera expresa y por escrito se hayan dispensado
              recíprocamente del deber de confidencialidad, por lo que si la
              otra parte considera que su respuesta debe ser confidencial, no
              podrás aportar al proceso la parte confidencial de esta oferta
              vinculante ni su respuesta
            </TooltipText>
          </HelpButtonContainer>
        </Label>
        <SelectContainer>
          <Select
            id="confidencialidad"
            value={confidencialidad.toString()}
            onChange={handleToggleConfidentiality}
            disabled={isReadOnly}
          >
            <option value="true">Sí</option>
            <option value="false">No</option>
          </Select>
          <SelectArrow>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
              <polygon points="12 17 4 7 20 7" />
            </svg>
          </SelectArrow>
        </SelectContainer>
      </ToggleContainer>
    );
  };

  const handleAcceptTerms = () => {
    setAceptaCondiciones(!aceptaCondiciones);

    // Mark as touched
    markInteracted();
    const newTouched = { ...touched };
    newTouched.aceptaCondiciones = true;
    setTouched(newTouched);
  };

  // Function to handle user interactions
  const markInteracted = () => {
    // No need to track interactions in Step 2 like in Step 1,
    // but the function is needed for handleAcceptTerms
  };

  return (
    <FormContainer>
      <HorizontalSections>
        {/* Sección de consideraciones de la oferta */}
        <FormSection isFirst>
          <FormWrapper>
            <SectionTitle>Consideraciones de la oferta</SectionTitle>
            <SectionDescription>
              Detalla tu oferta para la contraparte. Antes de enviarla te
              mostraremos una vista preliminar del documento que enviaremos
            </SectionDescription>

            {renderField(
              "referencia",
              "Referencia propia",
              referencia,
              (e) => setReferencia(e.target.value),
              errors.referencia,
              "text",
              false
            )}

            {renderField(
              "objeto",
              "Objeto de la controversia",
              objeto,
              (e) => setObjeto(e.target.value),
              errors.objeto
            )}

            <HorizontalField>
              <Label htmlFor="cuantia" required>
                Cuantía objeto de la controversia
                <HelpButtonContainer>
                  <HelpButton type="button">i</HelpButton>
                  <TooltipText>
                    Este dato quedará oculto si la oferta es confidencial. Recuerde que debe estar colegiado para enviar ofertas mayores de 2000€
                  </TooltipText>
                </HelpButtonContainer>
              </Label>
              <div style={{ position: 'relative', width: '100%' }}>
                <Input
                  type="text"
                  id="cuantia"
                  value={cuantia}
                  onChange={handleCuantiaChange}
                  placeholder="0,00"
                  onBlur={handleCuantiaBlur}
                  readOnly={isReadOnly}
                  style={{ paddingRight: '30px' }}
                />
                <div style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  pointerEvents: 'none',
                  color: '#666',
                  fontSize: '14px'
                }}>
                  €
                </div>
              </div>
              {errors.cuantia && touched.cuantia && <ErrorMessage>{errors.cuantia}</ErrorMessage>}
            </HorizontalField>

            {renderConfidentialityToggle()}

            {showConfidentialityWarning && (
              <InfoMessage>
                El artículo 9.2 a) de la L.O. 1/2025 establece que sólo puede
                dispensarse la confidencialidad cuando todas las partes (o sus
                abogados/as) de manera expresa y por escrito se hayan dispensado
                recíprocamente del deber de confidencialidad, por lo que si la
                otra parte considera que su respuesta debe ser confidencial, no
                podrás aportar al proceso la parte confidencial de esta oferta
                vinculante ni su respuesta
              </InfoMessage>
            )}

            {renderField(
              "plazoRespuesta",
              "Plazo de respuesta",
              plazoRespuesta,
              (e) => setPlazoRespuesta(e.target.value),
              errors.plazoRespuesta,
              "date",
              true,
              "De acuerdo con lo expuesto en el artículo 17.4 de la L.O 1/2025, el plazo mínimo que debes darle a tu contraparte es de 1 (un) mes pero puedes darle más plazo si lo deseas."
            )}
            
            <InfoMessage>
              Recuerda que para formular Ofertas Vinculantes cuya cuantía objeto de la controversia supere los 2.000€, la Ley exige la asistencia letrada
            </InfoMessage>
            
            <RequiredMessage>Todos los campos son obligatorios</RequiredMessage>
          </FormWrapper>
        </FormSection>

        {/* Sección para redactar la oferta */}
        <FormSection>
          <FormWrapper>
            <SectionTitle>Redactar oferta</SectionTitle>
            <SectionDescription>
              Puedes redactar la oferta directamente o adjuntar el documento
              digital en formato pdf. Solo puedes usar uno de los dos
              métodos.
            </SectionDescription>

            <FileInputHidden
              type="file"
              id="file-upload"
              accept=".pdf"
              onChange={handleFileChange}
              disabled={!isRichTextEmpty(contenido) || (existingFiles && existingFiles.length > 0) || isReadOnly}
            />

            {(!isRichTextEmpty(contenido) || (existingFiles && existingFiles.length > 0)) ? (
              <DisabledAdjuntarButton
                onClick={() => {
                  // El botón ya indica visualmente que no se puede usar
                  // No es necesario mostrar alert ya que el usuario puede ver la restricción
                }}
                disabled={isReadOnly}
              >
                <ClipIcon>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
                  </svg>
                </ClipIcon>
                Adjuntar archivo (primero elimina el texto y archivos existentes)
                <ArrowIcon>
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                    <polygon points="12 17 4 7 20 7" />
                  </svg>
                </ArrowIcon>
              </DisabledAdjuntarButton>
            ) : (
              <AdjuntarButton
                onClick={() =>
                  !isReadOnly && document.getElementById("file-upload").click()
                }
                disabled={isReadOnly}
              >
                <ClipIcon>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
                  </svg>
                </ClipIcon>
                Adjuntar archivo
                <ArrowIcon>
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                    <polygon points="12 17 4 7 20 7" />
                  </svg>
                </ArrowIcon>
              </AdjuntarButton>
            )}

            {/* Mostrar archivos existentes de la API */}
            {existingFiles && existingFiles.length > 0 && (
              <div>
                {existingFiles.map((file) => (
                  <FilePreview key={file.id}>
                    <FileIcon>📄</FileIcon>
                    <FileName>{file.name}</FileName>
                    {!isReadOnly && (
                      <RemoveFileButton
                        onClick={() => handleDeleteExistingFile(file.id)}
                        disabled={isDeletingFile}
                        title="Eliminar archivo"
                      >
                        {isDeletingFile ? "..." : "✕"}
                      </RemoveFileButton>
                    )}
                  </FilePreview>
                ))}
              </div>
            )}

            {archivo ? (
              <>
                <FilePreview>
                  <FileIcon>📄</FileIcon>
                  <FileName>{archivo.name}</FileName>
                  {!isReadOnly && (
                    <RemoveFileButton
                      onClick={() => handleRemoveFile()}
                      disabled={isReadOnly}
                    >
                      ✕
                    </RemoveFileButton>
                  )}
                </FilePreview>
                <RichTextEditor
                  id="oferta-content"
                  value=""
                  placeholder="Para escribir texto, elimina primero el archivo adjunto"
                  disabled={true}
                  readOnly={true}
                  minHeight="200px"
                />
              </>
            ) : (existingFiles && existingFiles.length > 0) ? (
              <>
                <RichTextEditor
                  id="oferta-content"
                  value={contenido}
                  onChange={(newContent) => {
                    console.log("Editor content updated:", {
                      oldValue: contenido,
                      newValue: newContent,
                      length: newContent.length,
                      timestamp: new Date().toISOString(),
                    });

                    // Update both local state and parent component state
                    setContenido(newContent);
                    updateFormData("contenido", newContent);
                  }}
                  placeholder={
                    isReadOnly 
                      ? "Contenido de la oferta"
                      : "Para modificar el texto, elimina primero los archivos adjuntos"
                  }
                  disabled={!isReadOnly ? true : false}
                  readOnly={!isReadOnly ? true : isReadOnly}
                  minHeight="200px"
                />
              </>
            ) : (
              <RichTextEditor
                id="oferta-content"
                value={contenido}
                onChange={(newContent) => {
                  console.log("Editor content updated:", {
                    oldValue: contenido,
                    newValue: newContent,
                    length: newContent.length,
                    timestamp: new Date().toISOString(),
                  });

                  // Update both local state and parent component state
                  setContenido(newContent);
                  updateFormData("contenido", newContent);
                }}
                placeholder="Redacta aquí la oferta"
                readOnly={isReadOnly}
                minHeight="300px"
              />
            )}

            <div style={{ position: "relative", marginBottom: "1.5rem" }}>
              {errors.contenido && touched.contenido && (
                <ErrorMessage style={{ bottom: "-1.5rem", right: "0" }}>
                  {errors.contenido}
                </ErrorMessage>
              )}
              {errors.archivo && touched.archivo && (
                <ErrorMessage style={{ bottom: "-1.5rem", right: "0" }}>
                  {errors.archivo}
                </ErrorMessage>
              )}
            </div>

            {/* Confirmación obligatoria */}
          </FormWrapper>
        </FormSection>
      </HorizontalSections>

      {/* Botones de acción */}
      {!isReadOnly && (
        <ButtonArea>
          <ConfirmationCheckbox>
            <CheckboxInput
              type="checkbox"
              checked={aceptaCondiciones}
              onChange={handleAcceptTerms}
              id="terms-checkbox"
              disabled={isSubmitting || isReadOnly}
            />
            <label htmlFor="terms-checkbox">
              Acepto los{" "}
              <a href="https://ofertasvinculantes.es/aviso-legal" target="_blank" rel="noopener noreferrer">
                 Aviso legal
              </a>{" "}
              y confirmo que he leído la{" "}
              <a href="https://ofertasvinculantes.es/politica-de-privacidad" target="_blank" rel="noopener noreferrer">Política de privacidad</a>
            </label>
          </ConfirmationCheckbox>
          <div
            style={{
              position: "relative",
              marginBottom: "1.5rem",
              width: "100%",
            }}
          >
            {errors.aceptaCondiciones && touched.aceptaCondiciones && (
              <ErrorMessage style={{ bottom: "-1.5rem", right: "0" }}>
                {errors.aceptaCondiciones}
              </ErrorMessage>
            )}
          </div>
          <PreviewLinkContainer>
            <PreviewButton onClick={handlePreview}>Vista previa</PreviewButton>
          </PreviewLinkContainer>
          <ButtonContainer>
            <SecondaryButton
              onClick={prevStep}
              disabled={isSubmitting || isSavingDraft || (hasShownNetworkModal && !isOnline)}
            >
              Atrás
            </SecondaryButton>
            <SaveButton
              onClick={handleSaveDraft}
              disabled={isSubmitting || isSavingDraft || (hasShownNetworkModal && !isOnline)}
            >
              {isSavingDraft ? "Guardando..." : "Guardar Borrador"}
            </SaveButton>
            <PrimaryButton
              onClick={handleSubmit}
              disabled={
                !isFormValid ||
                !aceptaCondiciones ||
                isSubmitting ||
                isSavingDraft ||
                (hasShownNetworkModal && !isOnline)
              }
            >
              {isSubmitting ? "Enviando..." : "Enviar Oferta"}
            </PrimaryButton>
            <StepIndicator>Paso 2 de 2</StepIndicator>
          </ButtonContainer>
        </ButtonArea>
      )}
      {/* Preview Modal */}
      <PreviewModal
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
        formData={{
          ofertante: formData.ofertante,
          contraparte: formData.contraparte,
          representacionContraparte: formData.representacionContraparte,
          referencia,
          objeto,
          cuantia,
          confidencialidad,
          plazoRespuesta,
          contenido: getPreviewContent(),
          archivo,
          existingFiles,
        }}
        user={user}
      />
    </FormContainer>
  );
};

export default OfertaStep2;
